import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { apiEndpoints, httpMethods } from "../../../constants/api.constants";
import useAPI from "../../../hooks/useAPI";
import useI18N from "../../../hooks/useI18N";
import { Logo } from "../../../shared/icon";
import { MUIStyled } from "../../../shared/MUIStyled";
import SABox from "../../../shared/SABox";
import SAButton from "../../../shared/SAButton";
import SALoader from "../../../shared/SALoader";
import SAStack from "../../../shared/SAStack";
import SATypography from "../../../shared/SATypography";
import SubscriptionMessageModal from "./SubscriptionMessageModal";

const RegistrationWrapper = MUIStyled(SAStack, {
  shouldForwardProp: (prop) => prop !== "step",
})(({ theme }) => ({
  paddingTop: "0px !important",
  padding: 20,
  minHeight: "100vh",
  backgroundColor: "#111",
  [theme.breakpoints.down("sm")]: {
    background: `linear-gradient(to bottom, #111 240px, ${theme.palette.white.main} 15%)`,
  },
}));

const GOLD_COLOR = "#C5A56F";

const StyledTitle = MUIStyled(SATypography)(({ theme, dir }) => ({
  fontSize: 24,
  marginTop: theme.spacing(2),
  fontWeight: 700,
  textAlign: dir === "rtl" ? "right" : "left",
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: { fontSize: 20, fontWeight: 600 },
}));

const StyledSubtitle = MUIStyled(SATypography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: GOLD_COLOR,
  marginBottom: theme.spacing(3),
  background: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  letterSpacing: "0.2px",
  border: `1px solid ${GOLD_COLOR}`,
  [theme.breakpoints.down("sm")]: {
    fontSize: 14,
    padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  },
}));

const FormGroup = MUIStyled("div")(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& label": {
    display: "block",
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  "& input": {
    width: "100%",
    padding: "12px 16px",
    border: `1.5px solid ${theme.palette.gray.light500}`,
    borderRadius: 0,
    fontSize: "1rem",
    transition: "all 0.2s",
    "&:focus": {
      outline: "none",
      borderColor: GOLD_COLOR,
      boxShadow: "0 2px 12px rgba(197,165,111,0.10)",
    },
    "&::placeholder": { color: theme.palette.gray.light300 },
  },
}));

const StyledPayButton = MUIStyled(SAButton)(({ theme }) => ({
  borderRadius: 0,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
}));

const StyledProcessingContainer = MUIStyled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  textAlign: "center",
  minHeight: "400px",
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
}));

const StyledProcessingTitle = MUIStyled(SATypography)(({ theme }) => ({
  color: "#a78a58",
  fontWeight: 600,
  fontSize: 28,
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: { fontSize: 24 },
}));

const StyledLoadingSpinner = MUIStyled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  margin: theme.spacing(3, 0),
  "& span": {
    width: 12,
    height: 12,
    backgroundColor: "#a78a58",
    borderRadius: "50%",
    display: "inline-block",
    animation: "pulse 1.4s infinite ease-in-out",
    "&:nth-of-type(1)": { animationDelay: "0s" },
    "&:nth-of-type(2)": { animationDelay: "0.2s" },
    "&:nth-of-type(3)": { animationDelay: "0.4s" },
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 0.6, transform: "scale(0.6)" },
    "50%": { opacity: 1, transform: "scale(1)" },
  },
}));

const THREEDS_AUTH_FAILED_STATUSES = [
  "AUTHENTICATION_REJECTED",
  "AUTHENTICATION_UNAVAILABLE",
  "AUTHENTICATION_NOT_SUPPORTED",
];

const AddPaymentCardDetails = () => {
  const { api } = useAPI();
  const { t, dir } = useI18N();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [threeDSHtml, setThreeDSHtml] = useState("");
  const [updatedSessionId, setUpdatedSessionId] = useState("");
  const [error, setError] = useState({});
  const [credimaxSession, setCredimaxSession] = useState(null);
  const [isSuccessResponse, setIsSuccessResponse] = useState(false);
  const [isErrorMessage, setIsErrorMessage] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] =
    useState(null);
  const [suppressAuto3DS, setSuppressAuto3DS] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const showSuccess = () => {
    setIsErrorMessage(false);
    setIsSuccessResponse(true);
    setSubscriptionModalOpen(true);
  };

  const showError = (key) => {
    if (finalizing) return;
    setIsSuccessResponse(false);
    setIsErrorMessage(true);
    setSubscriptionErrorMessage(key || "card-details-generic-error-message");
    setSubscriptionModalOpen(true);
  };

  const loaders = useSelector((state) => ({
    makePayment: state.loader?.["make-payment"],
    createSession: state.loader?.["create-session"],
    updateCard: state.loader?.["update-card"],
  }));

  const { order_id, transaction_id } = credimaxSession || {};

  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get("t");
  const authToken = urlToken;
  const refreshToken = urlParams.get("refresh_token");

  // Persist access token across redirects (e.g. 3DS OTP flow)
  if (urlToken || refreshToken) {
    sessionStorage.setItem("accessToken_addCard", urlToken);
    sessionStorage.setItem("refreshToken_addCard", refreshToken);
  }

  const storedToken =
    authToken || sessionStorage.getItem("accessToken_addCard");

  const deepLink = `sooq://onboard-complete?access_token=${storedToken}`;
  const fallbackUrl = `/onboard-complete?access_token=${storedToken}`;

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Handle redirect back from 3DS OTP which includes status param
  useEffect(() => {
    const skip = sessionStorage.getItem("skip3DSBack");
    if (skip) {
      sessionStorage.removeItem("skip3DSBack");
      setSuppressAuto3DS(true);
    }
    const authStatus = new URLSearchParams(window.location.search).get(
      "status",
    );

    if (authStatus) {
      const params = new URLSearchParams(window.location.search);
      params.delete("status");
      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", newUrl);

      if (authStatus === "failure") {
        showError("card-details-generic-error-message");
      } else if (authStatus === "success") {
        const sessionId = sessionStorage.getItem("credimax_session_id");
        if (!finalizing) {
          setIsErrorMessage(false);
          setSubscriptionErrorMessage(null);
          setSubscriptionModalOpen(false);
          setFinalizing(true);
          finalizeSaveCard(sessionId || null).finally(() => {
            setFinalizing(false);
          });
        }
      }
    }
    // run once on mount
  }, []);

  const startNewCredimaxSession = async () => {
    const response = await api({
      method: httpMethods.post,
      endPoint: apiEndpoints.cardDetailsCreateSession,
      headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {},
      needLoader: true,
      loader: "create-session",
    });

    if (response?.status) {
      setCredimaxSession(response?.data);
      const { session_id } = response?.data || {};
      if (session_id) {
        sessionStorage.setItem("credimax_session_id", session_id);
        initiateHostedSession(session_id);
      }
    } else {
      showError("card-details-generic-error-message");
    }
  };

  useEffect(() => {
    const hasStatus = new URLSearchParams(window.location.search).get("status");
    const existingId = sessionStorage.getItem("credimax_session_id");
    if (!hasStatus && !subscriptionModalOpen && !existingId && !finalizing) {
      startNewCredimaxSession();
    }
  }, []);

  useEffect(() => {
    if (updatedSessionId && !suppressAuto3DS) {
      initiateAuthentication();
    }
  }, [updatedSessionId, suppressAuto3DS]);

  const mapCredimaxErrors = (errors) => {
    const fieldMap = {
      cardNumber: "card-number",
      expiryMonth: "expiry-month",
      expiryYear: "expiry-year",
      securityCode: "cvv",
    };

    const messageMap = { missing: "required", invalid: "invalid" };

    const mappedErrors = {};

    Object.entries(errors).forEach(([key, type]) => {
      const fieldKey = fieldMap[key];
      const msgKey = `${fieldKey}-${messageMap[type]}`;
      mappedErrors[key] = t(msgKey);
    });

    return mappedErrors;
  };

  const initiateHostedSession = (sessionId) => {
    window.PaymentSession.configure({
      session: sessionId,
      fields: {
        card: {
          number: "#card-number",
          securityCode: "#security-code",
          expiryMonth: "#expiry-month",
          expiryYear: "#expiry-year",
        },
      },
      interaction: { displayControl: { formatCard: "EMBOSSED" } },
      frameEmbeddingMitigation: ["javascript"],
      callbacks: {
        initialized: (response) => {
          if (response?.status !== "ok") {
            showError("card-details-session-request-error");
          }
        },
        formSessionUpdate: (response) => {
          if (response?.status) {
            if (response?.status === "ok") {
              setUpdatedSessionId(response.session.id);
              sessionStorage.setItem(
                "credimax_session_id",
                response.session.id,
              );
              setError({});
            } else if (response?.status === "fields_in_error") {
              const mapped = mapCredimaxErrors(response?.errors);
              setError(mapped);
            } else {
              showError("card-details-session-request-error");
            }
          } else {
            showError("card-details-session-request-error");
          }
        },
      },
    });
  };

  const initiateAuthentication = async () => {
    setLoading(true);

    try {
      if (!order_id || !transaction_id) {
        showError("card-details-session-request-error");
        setLoading(false);
        return;
      }

      window.ThreeDS.configure({
        merchantId: process.env.REACT_APP_CREDIMAX_MERCHANT_ID,
        sessionId: updatedSessionId,
        containerId: "3DSUI",
        configuration: { userLanguage: "en-AU", wsVersion: 100 },
        callback: () => {
          if (!window.ThreeDS.isConfigured()) {
            showError("3ds-auth-init-failed");
          }
        },
      });

      window.ThreeDS.initiateAuthentication(
        `${order_id}`,
        `${transaction_id}`,
        (data) => {
          if (data && data.error) {
            showError("3ds-auth-init-failed");
            setLoading(false);
          } else {
            const authenticationStatus =
              data?.restApiResponse?.order?.authenticationStatus;

            if (authenticationStatus === "AUTHENTICATION_AVAILABLE") {
              authenticatePayer();
            } else if (
              THREEDS_AUTH_FAILED_STATUSES.includes(authenticationStatus) &&
              data?.restApiResponse?.order?.status ===
                "AUTHENTICATION_UNSUCCESSFUL"
            ) {
              showError("3ds-auth-failed");
              setLoading(false);
            } else {
              // No further authentication required, finalize
              finalizeSaveCard();
              setLoading(false);
            }
          }
        },
        { sourceOfFunds: { type: "CARD" } },
      );
    } catch {
      setLoading(false);
    }
  };

  const authenticatePayer = async () => {
    try {
      window.ThreeDS.authenticatePayer(
        `${order_id}`,
        `${transaction_id}`,
        async (data) => {
          if (!data.error) {
            const payerInteractionStatus =
              data?.restApiResponse?.authentication?.payerInteraction;
            const authenticationStatus =
              data?.restApiResponse?.order?.authenticationStatus;

            if (payerInteractionStatus === "REQUIRED") {
              sessionStorage.setItem("skip3DSBack", "1");
              setThreeDSHtml(data?.htmlRedirectCode);
            } else if (
              THREEDS_AUTH_FAILED_STATUSES.includes(authenticationStatus) &&
              data?.restApiResponse?.order?.status ===
                "AUTHENTICATION_UNSUCCESSFUL"
            ) {
              showError("3ds-auth-failed");
            } else {
              await finalizeSaveCard();
            }
          } else {
            showError("3ds-auth-failed");
          }
          setLoading(false);
        },
      );
    } catch {
      showError("3ds-auth-failed");
      setLoading(false);
    }
  };

  const finalizeSaveCard = async (sessionId = null) => {
    const payload = {
      session_id: sessionId || updatedSessionId,
    };

    // prefer current URL token, fall back to session stored token (survives redirect)
    const token = authToken || sessionStorage.getItem("accessToken_addCard");

    const response = await api({
      method: httpMethods.post,
      endPoint: apiEndpoints.updateUserCardDetails,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      data: payload,
      needLoader: true,
      loader: "update-card",
      showToastOnError: false,
    });

    if (response?.status) {
      sessionStorage.removeItem("credimax_session_id");
      sessionStorage.removeItem("makeDefault_addCard");

      showSuccess("card-details-success-message");
    } else {
      showError(
        response?.error?.response?.data?.error?.detail?.session_id ||
          "failed-save-card-details",
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await window.PaymentSession.updateSessionFromForm("card");

      const sessionId =
        updatedSessionId || sessionStorage.getItem("credimax_session_id");
      if (!sessionId) {
        showError("card-details-session-request-error");

        return;
      }
      // If we came back from 3DS via back-button, don't auto-start 3DS until user explicitly submits
      if (suppressAuto3DS) {
        setSuppressAuto3DS(false);
        initiateAuthentication();
      }
    } catch {
      showError("card-details-session-request-error");
    }
  };

  const handleClose = () => {
    if (isSuccessResponse) {
      if (isMobile) {
        window.location.href = deepLink || fallbackUrl;
        sessionStorage.removeItem("accessToken_addCard");
        return;
      } else {
        navigate("/onboard-complete");
        sessionStorage.removeItem("accessToken_addCard");
        return;
      }
    }

    const params = new URLSearchParams(window.location.search);
    params.delete("status");
    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");
    window.history.replaceState({}, "", newUrl);
    setIsErrorMessage(false);
    setSubscriptionModalOpen(false);
    setSubscriptionErrorMessage(null);
    sessionStorage.removeItem("credimax_session_id");
    startNewCredimaxSession();
  };

  return (
    <>
      <RegistrationWrapper
        alignItems="center"
        justifyContent={{ sm: "center" }}
      >
        <SABox mb={3} mt={{ xs: 5, sm: 2 }} textAlign="center">
          <Logo />
        </SABox>
        <SAStack
          width="100%"
          sx={{ "&:has(.language)": { flex: "none" } }}
          maxWidth={{
            sm: 600,
            xs: "100%",
            padding: "14px",
            backgroundColor: "#F9FAFB",
            border: "1px solid #D2D5DA",
          }}
        >
          <SABox sx={{ width: "100%" }}>
            {loading || loaders?.makePayment ? (
              <StyledProcessingContainer>
                <StyledProcessingTitle>
                  {t("saving-card-details-title")}
                </StyledProcessingTitle>
                <StyledLoadingSpinner>
                  <span></span>
                  <span></span>
                  <span></span>
                </StyledLoadingSpinner>
              </StyledProcessingContainer>
            ) : loaders?.createSession || loaders?.updateCard ? (
              <StyledProcessingContainer>
                <SALoader sx={{ display: "flex", justifyContent: "center" }} />
              </StyledProcessingContainer>
            ) : (
              <>
                <StyledTitle dir={dir} variant="h6">
                  {t("card-details")}
                </StyledTitle>
                <StyledSubtitle>{t("card-details-subtitle")}</StyledSubtitle>
                <form onSubmit={handleSubmit}>
                  <FormGroup>
                    <label htmlFor="cardNumber">{t("card-number")}</label>
                    <input
                      type="text"
                      id="card-number"
                      placeholder="•••• •••• •••• ••••"
                      readOnly
                    />
                    {error?.cardNumber && (
                      <SATypography variant="caption" color="error">
                        {error?.cardNumber}
                      </SATypography>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <label htmlFor="expiryMonth">{t("expiry-month")}</label>
                    <input
                      type="text"
                      id="expiry-month"
                      placeholder="MM"
                      readOnly
                    />
                    {error?.expiryMonth && (
                      <SATypography variant="caption" color="error">
                        {error?.expiryMonth}
                      </SATypography>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <label htmlFor="expiryYear">{t("expiry-year")}</label>
                    <input
                      type="text"
                      id="expiry-year"
                      placeholder="YY"
                      readOnly
                    />
                    {error?.expiryYear && (
                      <SATypography variant="caption" color="error">
                        {error?.expiryYear}
                      </SATypography>
                    )}
                  </FormGroup>
                  <FormGroup>
                    <label htmlFor="securityCode">{t("cvv")}</label>
                    <input
                      type="password"
                      id="security-code"
                      placeholder="XXX"
                      readOnly
                    />
                    {error?.securityCode && (
                      <SATypography variant="caption" color="error">
                        {error?.securityCode}
                      </SATypography>
                    )}
                  </FormGroup>
                  <StyledPayButton fullWidth type="submit" variant="contained">
                    {t("save-card-details")}
                  </StyledPayButton>
                </form>
              </>
            )}
          </SABox>
          <div id="3DSUI">
            {threeDSHtml && <ThreeDSChallenge html={threeDSHtml} />}
          </div>
        </SAStack>
      </RegistrationWrapper>
      <SubscriptionMessageModal
        subscriptionModalOpen={subscriptionModalOpen}
        isSuccesResponse={isSuccessResponse}
        isErrorMessage={isErrorMessage}
        subscriptionErrorMessage={subscriptionErrorMessage}
        successTitle="card-details-success-message"
        successSubtitle="card-details-success-subtitle"
        errorTitle="card-details-error-message-title"
        onAfterPayment={handleClose}
        goToFinalStep={handleClose}
        session={null}
      />
    </>
  );
};

const ThreeDSChallenge = ({ html }) => {
  useEffect(() => {
    if (html) {
      document.open();
      document.write(html);
      document.close();
    }
  }, [html]);

  return null;
};

export default AddPaymentCardDetails;
